<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { useI18n } from "vue-i18n"
import { computed, ref } from "vue"

// Helpers
import { formatNumber } from "@core/helpers"

// Store
import { useSourceStore } from "@core/stores/source.store"

// Composables
import { useSuppliers } from "@core/composables"

// Types
import type { InformationPDL, LineBlockLine, OldOffrePdl } from "@types"

// Constant
import { GRID_OFFER_CODES } from "@core/constants"

const sourceStore = useSourceStore()
const { getSuppliers, isLoadingSuppliers } = useSuppliers()

// ================================
// INTERNATIONALIZATION
// ================================
const { t, tm } = useI18n({
    messages: {
        fr: {
            supplier: "Fournisseur",
            offer: [
                {
                    text: "Prix d'abonnement HTT",
                    tooltip: "HTT (Hors Toute Taxe)",
                    value: "prix_abonnement",
                    unit: "€/mois",
                },
            ],
            timeline: {
                saison_haute: "saison haute",
                saison_basse: "saison basse",
                jours_bleu: "jours bleus",
                jours_blancs: "jours blancs",
                jours_rouge: "jours rouges",
            },
            price: {
                text: ({ named }: { named: (key: string) => string }) => {
                    switch (named("offer")) {
                        case GRID_OFFER_CODES.TEMPO:
                        case GRID_OFFER_CODES.HPHC:
                            return "Grille tarifaire HTT"
                        case GRID_OFFER_CODES.BASE:
                            return "Tarif kWh Base HTT"
                        default:
                            return "Tarif kWh moyen HTT"
                    }
                },
                tooltip: ({ named }: { named: (key: string) => string }) => {
                    switch (named("offer")) {
                        case GRID_OFFER_CODES.TEMPO:
                        case GRID_OFFER_CODES.HPHC:
                            return "HTT (Hors Toute Taxe)\n\nLa saison haute (suffixe H) définie par ENEDIS commence le 1er novembre et se termine le 31 mars inclus.\n\nLa saison basse (suffixe E) définie par ENEDIS commence le 1er avril et se termine le 31 octobre inclus."
                        default:
                            return "HTT (Hors Toute Taxe)"
                    }
                },
                HP: "HP",
                HC: "HC",
                HP_tooltip: "Heures Pleines",
                HC_tooltip: "Heures Creuses",
                H: "saison haute",
                B: "saison basse",
                BU: "jours bleus",
                BC: "jours blancs",
                R: "jours rouges",
            },
            button: {
                modify: "Modifier la tarification et le fournisseur",
                add_offer: "Ajouter la tarification et le fournisseur",
            },
            warning: {
                non_teleoperable:
                    "Compteur non téléopérable : la comparaison d’offres et l’analyse sur la consommation ne sont pas disponibles. Les tarifs et la simulation sur courbe de charge / données journalières ne s’appliquent pas.",
                no_pricing_soutirage:
                    "Actuellement, nous ne permettons pas d'ajouter des tarifs pour les compteurs n'ayant pas de soutirage",
                no_pricing:
                    "Actuellement, nous ne calculons pas le cout total de l’abonnement et de la consommation d’électricité pour les compteurs C2.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    meterId: string
}

const injectionSoutirageMap: Record<string, string> = {
    P4: "C5",
    P3: "C4",
    P1: "C2",
    null: "",
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()
const modalEditOffer = ref<boolean>(false)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * formatte et affiche les données simples ( qui ne nécessite pas de formattage complexes selon les offres)
 */
const getOfferPDL = computed(() => {
    const pdl = getSourceById?.value?.informations?.pdl as InformationPDL
    if (!pdl?.structure_tarifaire?.offre) {
        return null
    }
    const lines = tm("offer") || []
    const formattedLine: { line: LineBlockLine; loading?: boolean }[] = []

    lines.forEach(
        (
            line: {
                text: string
                value: string
                unit?: string
                tooltip?: string
            },
            index: number
        ) => {
            formattedLine[index] = {
                line: {
                    text: line.text,
                    tooltip: line?.tooltip,
                    value: formatValue(
                        (pdl?.structure_tarifaire?.offre?.[
                            line.value as keyof OldOffrePdl
                        ] as string) || "", // Provide a default empty string
                        line?.unit
                    ),
                },
            }
        }
    )

    const supplier = getSuppliers.value?.find(
        (s) =>
            s._id_supplier ===
            pdl?.structure_tarifaire?.offre?._id_energy_supplier
    )

    formattedLine.unshift({
        line: {
            text: t("supplier"),
            value: supplier?.name || "-",
        },
        loading: isLoadingSuppliers.value,
    })

    // Ajout de la ligne pour les tarifs avec slot
    formattedLine.push({
        line: {
            id: "price_grid",
            text: t("price.text", {
                offer: pdl?.structure_tarifaire?.offre?.grille_tarifaire || "",
            }),
            tooltip: t("price.tooltip", {
                offer: pdl?.structure_tarifaire?.offre?.grille_tarifaire || "",
            }),
            value: null,
        },
    })

    return formattedLine
})

const hasSoutirage = computed(() => {
    return getSourceById.value?.informations?.pdl?.data?.soutirage
        ?.has_soutirage
})

/** PDL pour lequel Enedis indique une absence de téléopérabilité. */
const getIsPdlNonTeleoperable = computed(
    () =>
        getSourceById.value?.informations?.pdl?.compteur?.teleoperable === false
)

const isSegmentC4OrC5 = computed(() => {
    const segment = getSourceById.value?.informations?.pdl?.compteur?.segment
    const mappedSegment = injectionSoutirageMap[segment || "null"]

    return (
        ["C4", "C5"].includes(segment || "") ||
        ["C4", "C5"].includes(mappedSegment)
    )
})

// ============================================
// Logic de formattage des données `dynamique`
// ============================================

/**
 *
 * @param prices Regroupe les prix par "saison" (dernière lettre du code grille)
 */
function groupBySeason(prices: { grille: string; prix_kwh: number }[]) {
    const seasons: Record<string, { grille: string; prix_kwh: number }[]> = {}

    prices.forEach((item) => {
        const season = item.grille.slice(-1) // on récupère la dernière lettre du code grille
        if (!seasons[season]) seasons[season] = [] // si la saison n’existe pas encore, on l’initialise
        seasons[season].push(item) // on ajoute l’élément dans le bon groupe
    })

    return seasons
}

// Regroupe les prix par préfixe (BC, BU, R...), en normalisant certains codes via une map
function groupByPrefix(prices: { grille: string; prix_kwh: number }[]) {
    const groups: Record<string, { grille: string; prix_kwh: number }[]> = {}

    // Table de correspondance pour unifier certains codes grille
    const mapPrefix: Record<string, string> = {
        BCHC: "BC",
        BCHP: "BC",
        BUHC: "BU",
        BUHP: "BU",
        RHC: "R",
        RHP: "R",
    }

    prices.forEach((item) => {
        // On cherche dans la map, sinon on prend les 2 premières lettres comme clé
        const prefix = mapPrefix[item.grille] || item.grille.slice(0, 2)
        if (!groups[prefix]) groups[prefix] = [] // init du groupe si nécessaire
        groups[prefix].push(item) // on ajoute l’élément dans le bon groupe
    })

    return groups
}
// Transforme les groupes en tableau alignant les prix HP (heures pleines) et HC (heures creuses)
type OfferGrilleRow = { grille: string; prix_kwh: number }

function zipGroups(groups: Record<string, OfferGrilleRow[]>) {
    const result: {
        season: string
        left: OfferGrilleRow | undefined
        right: OfferGrilleRow | undefined
    }[] = []

    Object.keys(groups)
        .sort()
        .forEach((seasonLetter) => {
            const group = groups[seasonLetter]
            // On cherche le tarif HP (soit commençant par HP, soit finissant par HP)
            const hp =
                group.find((item) => item.grille.startsWith("HP")) ||
                group.find((item) => item.grille.endsWith("HP"))
            // On cherche le tarif HP (soit commençant par HC, soit finissant par HC)
            const hc =
                group.find((item) => item.grille.startsWith("HC")) ||
                group.find((item) => item.grille.endsWith("HC"))

            if (hp || hc) {
                result.push({
                    season: seasonLetter,
                    left: {
                        grille: "HP",
                        prix_kwh: formatNumber(hp?.prix_kwh, {
                            unit: "kWh",
                            style: "currency",
                            maximumFractionDigits: 10,
                            autoCents: true,
                        }),
                    },
                    right: {
                        grille: "HC",
                        prix_kwh: formatNumber(hc?.prix_kwh, {
                            unit: "kWh",
                            style: "currency",
                            maximumFractionDigits: 10,
                            autoCents: true,
                        }),
                    },
                })
            }
        })

    return result
}

/**
 * Calcule et formate la grille des tarifs pour l'offre PDL courante (Tempo, HPHC ou autre).
 *
 * Retourne un tableau de lignes à afficher dans la carte "offre".
 * - Pour Tempo : groupe par préfixe et zip les groupes.
 * - Pour HPHC : si segment C4/C2, groupe par saison puis zip, sinon map simple.
 * - Sinon : map simple de toutes les grilles.
 */
const getPriceOfferPDL = computed<
    | {
          season: string
          left: { grille: string; prix_kwh: string }
          right: { grille: string; prix_kwh: string }
      }[]
    | { left: { prix_kwh: string } }[]
>(() => {
    const pdl = getSourceById?.value?.informations?.pdl
    const offre = pdl?.structure_tarifaire?.offre
    const grilles = offre?.prix_grille_tarifaire ?? []

    if (!offre) return []

    const formatLigne = (element: { grille: string; prix_kwh: number }) => ({
        left: {
            grille: element.grille,
            prix_kwh: formatNumber(element.prix_kwh, {
                unit: "kWh",
                style: "currency",
                maximumFractionDigits: 10,
                autoCents: true,
            }),
        },
    })

    switch (offre.grille_tarifaire) {
        case GRID_OFFER_CODES.TEMPO:
            // Offre Tempo : regroupe par préfixe pour aligner prix HP/HC/Hiver/Été/etc.
            return zipGroups(groupByPrefix(grilles))

        case GRID_OFFER_CODES.HPHC:
            // HPHC : différencie si segment C4/C2 pour groupement par saison
            if (["C4", "C2"].includes(pdl?.compteur?.segment || "")) {
                return zipGroups(groupBySeason(grilles))
            }
            return grilles.map(formatLigne)

        default:
            // Offre classique ou inconnue : tableau simple (une seule ligne par prix)
            return grilles.map((item) => ({
                left: {
                    prix_kwh: formatNumber(item.prix_kwh, {
                        unit: "kWh",
                        style: "currency",
                        maximumFractionDigits: 10,
                        autoCents: true,
                    }),
                },
            }))
    }
})

// ================================
// METHODS
// ================================

/**
 * format les valeurs
 * @param value valeur
 * @param unit unité
 */
function formatValue(value: string, unit?: string): string {
    if (value === null || value === undefined) return "-"
    else if (unit) return `${value} ${unit}`
    else return value
}

/**
 * Ouvre la modal d'édition des tarifs
 */
const onOpenModal = () => {
    modalEditOffer.value = true
}

/**
 * Plages horaires extraites de la source sélectionnée
 */
const getPlagesHoraires = computed(
    () =>
        getSourceById.value?.informations?.pdl?.comptage?.plages_horaires ?? {
            heures_creuses: [],
            heures_pointe_fixe: [],
            pointes: [],
        }
)
</script>

<template>
    <div class="meter_card_offer_pdl">
        <BaseMessage
            v-if="getIsPdlNonTeleoperable"
            severity="error"
            class="mb-6 w-full"
        >
            {{ t("warning.non_teleoperable") }}
        </BaseMessage>
        <!-- on affiche les heures pleines et creuses d'un compteur si on en à -->
        <div
            v-if="
                getPlagesHoraires.heures_creuses ||
                getPlagesHoraires.heures_pointe_fixe
            "
            class="mb-16"
        >
            <MeterTimeline
                :heures-creuses="getPlagesHoraires.heures_creuses"
                :heures-pointes-fixes="getPlagesHoraires.heures_pointe_fixe"
            />
        </div>
        <!-- on affiche les données -->
        <div v-if="getOfferPDL">
            <div v-for="({ line, loading }, index) in getOfferPDL" :key="index">
                <BaseLineBlock :line="line" :loading="loading">
                    <!-- Slot pour la grille tarifaire -->
                    <template #price_grid>
                        <div
                            v-if="getPriceOfferPDL"
                            class="flex flex-col gap-2 w-full"
                        >
                            <div
                                v-for="(pair, pairIndex) in getPriceOfferPDL"
                                :key="pairIndex"
                                class="flex flex-row gap-2"
                            >
                                <!-- affiche la saison si elle existe ou le nom de la grille ex: saison haute, jours blancs, ... -->
                                <p
                                    v-if="
                                        'season' in pair &&
                                        pair?.season &&
                                        t(`price.${pair.season}`) !==
                                            `price.${pair?.season}`
                                    "
                                    class="font-bold text-cyan-400"
                                >
                                    {{ t(`price.${pair?.season}`) }}・
                                </p>
                                <!-- les valeurs -->
                                <div class="flex flex-row gap-1 items-center">
                                    <!-- le nom de la grille : ex HP -->
                                    <p
                                        v-if="pair?.left?.grille"
                                        class="font-bold"
                                    >
                                        {{ t(`price.${pair.left.grille}`) }}
                                    </p>
                                    <BaseIcon
                                        v-if="
                                            t(
                                                `price.${pair.left?.grille}_tooltip`
                                            ) !==
                                            `price.${pair.left?.grille}_tooltip`
                                        "
                                        icon="pi pi-info-circle"
                                        style="font-size: 0.8rem"
                                        v-tooltip="
                                            t(
                                                `price.${pair.left?.grille}_tooltip`
                                            )
                                        "
                                        class="mr-1 inline-flex items-center"
                                    ></BaseIcon>
                                    <!-- la valeur associé -->
                                    <p v-if="pair?.left?.prix_kwh">
                                        {{ pair.left.prix_kwh }}
                                    </p>
                                </div>
                                <!-- le séparateur -->
                                <span
                                    v-if="pair?.right?.grille"
                                    class="font-bold text-cyan-400"
                                    >・</span
                                >
                                <div class="flex gap-1 items-center">
                                    <!-- le nom de la grille : ex HC -->
                                    <p
                                        v-if="pair?.right?.grille"
                                        class="font-bold"
                                    >
                                        {{ t(`price.${pair.right?.grille}`) }}
                                    </p>
                                    <BaseIcon
                                        v-if="
                                            t(
                                                `price.${pair.right?.grille}_tooltip`
                                            ) !==
                                            `price.${pair.right?.grille}_tooltip`
                                        "
                                        icon="pi pi-info-circle"
                                        style="font-size: 0.8rem"
                                        v-tooltip="
                                            t(
                                                `price.${pair.right?.grille}_tooltip`
                                            )
                                        "
                                        class="mr-1"
                                    ></BaseIcon>
                                    <!-- la valeur associé -->
                                    <p v-if="pair?.right?.prix_kwh">
                                        {{ pair.right.prix_kwh }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>
                </BaseLineBlock>
            </div>
            <!-- Bouton pour ouvrir la modal pour modifier les tarifs -->
            <div class="text-center mt-4">
                <BaseButton
                    :label="t('button.modify')"
                    variant="outlined"
                    severity="contrast"
                    icon="pi pi-pencil"
                    @click="onOpenModal"
                ></BaseButton>
            </div>
        </div>
        <!-- Bouton pour ouvrir la modal pour ajouter les tarifs -->
        <div v-else class="text-center">
            <BaseButton
                v-if="
                    isSegmentC4OrC5 && hasSoutirage && !getIsPdlNonTeleoperable
                "
                class="max-w-fit mx-auto mt-4"
                :label="t('button.add_offer')"
                variant="outlined"
                severity="contrast"
                icon="pi pi-pencil"
                @click="onOpenModal"
            ></BaseButton>
            <BaseMessage
                v-if="!hasSoutirage && !getIsPdlNonTeleoperable"
                severity="warn"
                class="w-full"
            >
                {{ t("warning.no_pricing_soutirage") }}
            </BaseMessage>
            <BaseMessage
                v-if="
                    !isSegmentC4OrC5 && hasSoutirage && !getIsPdlNonTeleoperable
                "
                severity="warn"
                class="w-full"
            >
                {{
                    t("warning.no_pricing", {
                        segment:
                            getSourceById?.informations?.pdl?.compteur?.segment,
                    })
                }}
            </BaseMessage>
        </div>
        <!-- modal d'ajout / d'édition des tarifs -->
        <DialogMeterEditOffer
            v-model:visible="modalEditOffer"
            :meter-id="meterId"
            type="pdl"
        ></DialogMeterEditOffer>
    </div>
</template>

<style scoped></style>
